import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type {
  GiftcardTheme,
  GiftcardThemeShowcase,
} from '~/composables/types/giftcardTheme'
import { mapGiftcardThemes } from '~/utils/giftcardThemes'

/**
 * Themed gift cards (designs, sample PDF title/message) managed in the back
 * office as the `kft-giftcard-themes` dynamic content. Returned, not awaited -
 * await it in `<script setup>` so SSR renders with the data. Pass
 * `targetLocale` to fetch another locale's themes (e.g. its slugs for the
 * locale switcher); defaults to the current locale.
 */
export const useGiftcardThemes = (targetLocale?: string) => {
  const { locale } = useI18n()
  const lang = targetLocale ?? locale.value

  return useKftApiFetch<
    KftApiDefaultResponse<GiftcardThemeShowcase[]>,
    GiftcardTheme[]
  >('/v1/store/gift-card/themes', {
    method: 'GET',
    // the API localizes titles/names via Accept-Language
    key: `giftcard-themes-${lang}`,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': `${convertLocaleIntoBCP47(lang)},${convertISO2Locale(lang)}`,
    },
    dedupe: 'defer',
    // the showcase sits on several page types - reuse the themes on client
    // navigation instead of refetching them on every page
    getCachedData: (key) => useNuxtData(key).data.value ?? undefined,
    transform: (response) => mapGiftcardThemes(response?.data ?? []),
  })
}
